/**
 * ShareSocialContent
 *
 * Content component for social sharing sheet.
 * Displays social media share buttons for sharing user profiles or content.
 */

import React, { useState } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import {
  FacebookShareButton,
  WhatsappShareButton,
  TwitterShareButton,
  EmailShareButton,
  TelegramShareButton,
  LinkedinShareButton,
  FacebookMessengerShareButton,
  RedditShareButton,
  FacebookIcon,
  WhatsappIcon,
  XIcon,
  EmailIcon,
  TelegramIcon,
  LinkedinIcon,
  FacebookMessengerIcon,
  RedditIcon,
} from 'react-share';
import { Link } from 'lucide-react';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ShareSocialContentProps {
  headerImageUrl: string | null;
  headerImageAlt: string;
  shareTitle: string;
  shareText: string;
  shareUrl: string;
}

export const ShareSocialContent: React.FC<ShareSocialContentProps> = ({
  headerImageUrl,
  headerImageAlt,
  shareTitle,
  shareText,
  shareUrl,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), ANIMATION_CONSTANTS.duration.normal);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  return (
    <div
      style={{
        paddingLeft: responsive.spacing.lg,
        paddingTop: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
        paddingBottom: responsive.spacing.lg,
      }}
    >
      {/* Header: image + title + text + url — mirrors native share card layout */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: responsive.spacing.base,
          marginBottom: responsive.spacing.lg,
        }}
      >
        {headerImageUrl && (
          <img
            src={headerImageUrl}
            alt={headerImageAlt}
            style={{
              // Specify only width to maintain aspect ratio
              width: responsive.avatar.medium,
              borderRadius: responsive.borderRadius.sm,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: responsive.spacing.xs }}>
          <h3
            style={{
              color: theme.text.primary,
              fontSize: responsive.font.lg,
              fontWeight: responsive.fontWeight.semibold,
              margin: 0,
            }}
          >
            {shareTitle}
          </h3>
          {shareText ? (
            <span
              style={{
                color: theme.text.secondary,
                fontSize: responsive.font.sm,
              }}
            >
              {shareText}
            </span>
          ) : null}
        </div>
      </div>

      {/* Spacer */}
      <div style={{ height: responsive.spacing['2xl'] }} />

      {/* Social share buttons */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          gap: responsive.spacing['xl'],
          flexWrap: 'wrap',
        }}
      >
        <FacebookShareButton {...qaAttr({ id: 'share-social-facebook' })} url={shareUrl} hashtag="#Expause">
          <FacebookIcon size={responsive.icon.xxlarge} round />
        </FacebookShareButton>

        <WhatsappShareButton {...qaAttr({ id: 'share-social-whatsapp' })} url={shareUrl} title={shareText}>
          <WhatsappIcon size={responsive.icon.xxlarge} round />
        </WhatsappShareButton>

        <TwitterShareButton {...qaAttr({ id: 'share-social-twitter' })} url={shareUrl} title={shareText} hashtags={['Expause']}>
          <XIcon size={responsive.icon.xxlarge} round />
        </TwitterShareButton>

        <TelegramShareButton {...qaAttr({ id: 'share-social-telegram' })} url={shareUrl} title={shareText}>
          <TelegramIcon size={responsive.icon.xxlarge} round />
        </TelegramShareButton>

        <LinkedinShareButton {...qaAttr({ id: 'share-social-linkedin' })} url={shareUrl} title={shareTitle} summary={shareText}>
          <LinkedinIcon size={responsive.icon.xxlarge} round />
        </LinkedinShareButton>

        <FacebookMessengerShareButton {...qaAttr({ id: 'share-social-messenger' })} url={shareUrl} appId="">
          <FacebookMessengerIcon size={responsive.icon.xxlarge} round />
        </FacebookMessengerShareButton>

        <RedditShareButton {...qaAttr({ id: 'share-social-reddit' })} url={shareUrl} title={shareTitle}>
          <RedditIcon size={responsive.icon.xxlarge} round />
        </RedditShareButton>

        <EmailShareButton {...qaAttr({ id: 'share-social-email' })} url={shareUrl} subject={shareTitle} body={shareText}>
          <EmailIcon size={responsive.icon.xxlarge} round />
        </EmailShareButton>

        {/* Copy Link Button */}
        <button
          {...qaAttr({ id: 'share-copy-link' })}
          onClick={handleCopyLink}
          style={{
            width: responsive.icon.xxlarge,
            height: responsive.icon.xxlarge,
            borderRadius: responsive.borderRadius.round,
            backgroundColor: copied ? theme.exclusive : theme.secondary,
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: `background-color ${ANIMATION_CONSTANTS.duration.normal}ms ${ANIMATION_CONSTANTS.easing.easeOut}`,
          }}
          title={copied ? l10n.common.copied : l10n.common.copy}
        >
          <Link
            size={responsive.icon.medium}
            color={theme.icon.primary}
            strokeWidth={responsive.border.medium}
          />
        </button>
      </div>

      {/* Spacer */}
      <div style={{ height: responsive.spacing['2xl'] }} />

    </div>
  );
};
